/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useLocation, useNavigate } from "react-router-dom";
import { hasNavigatedRestaurant, navigatedRestaurantGroupId, navigatedRestaurantGroupName, useNavigatedRestaurantFilter } from "../../hooks/useNavigatedRestaurantFilter";
import { invalidateAuditLogQueries } from "../../utils/invalidateAuditLogQueries";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Popover,
  Select,
  Typography,
} from "@mui/material";
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CachedIcon from "@mui/icons-material/Cached";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import api from "../../api";
import authenticatedFetch from "../../api/authenticatedFetch";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { StyledBreadCrumbContainer, StyledLink } from "../user_management/styles";
import { FilterContainer } from "../../components/filter_container";
import { StyledFilterCancel, StyledFormControl } from "../dashboard/styles";
import { StyledInputLabel } from "../restaurant/styles.jsx";
import { selectBaseStyles } from "../reports/styles";
import DropdownSearchBar from "../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../components/dropdown_infinite_loader";
import { SelectedMenuItem } from "../../components/selected_menu_item";
import { StyledMenuItem } from "../../components/styled_menu_item";
import { ResetButton } from "../../components/reset_button";
import { UpdateButton } from "../../components/update_button";
import { Loader } from "../../components/loader";
import { IndexTable } from "../../components/index_table";
import { LoadingSpinner } from "../../components/loading_spinner";
import { ConfirmationDialog } from "../../components/confirmation_dialog";
import { StyledSearchBar } from "../employee/styles.js";
import { AbilityContext } from "../../contexts/abilityContext";
import { useNotificationContext } from "../../contexts/notificationContext";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { invalidateQueryKeys } from "../../hooks/mutations/utils/invalidateQueries";
import { getMutationErrorMessage } from "../../hooks/mutations/utils/mutationErrorMessage";
import { serviceChargeMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/serviceChargeMutationKeys";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { Query_Keys } from "../../lib/queryKeys";
import { PerPage } from "../../constants/perPage";
import { strictCanScope } from "../../utils/caslStrict";
import { checkAdmin } from "../../utils/checkAdmin";
import camelize from "../../utils/camelize";
import { buildQueryParams } from "../../utils/buildQueryParams";
import { useDebounce } from "../../utils/useDebounce";
import { hasSingleItem } from "../../utils/hasSingleItem";
import { getSingleItemValue } from "../../utils/getSingleItemValue";
import {
  getDropdownNextPageParam,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../utils/dropdownInfiniteQuery";
import {
  FetchButtonContainer,
  actionSectionStyle,
  fetchButtonStyle,
  filterContainerStyle,
  filterSectionStyle,
  menuPropStyle,
  pageTitleStyle,
  titleSectionStyle,
  typographyStyle,
} from "../meal_periods/styles";
import {
  StatusMenuList,
  StyledStatusButton,
  statusMenuItemStyle,
  statusPopoverPaperStyle,
} from "../configured_discounts/styles";
import { ChargeTypePill, ServiceChargePage, StatusText } from "./styles";
import {
  CHARGE_TYPE_OPTIONS,
  STATUS_LABELS,
  chargeTypeLabel,
  formatOrdersCount,
  hasPastOrders,
  recalculatesPastOrders,
  typeChangeNote,
} from "./serviceChargeUtils";

const EMPTY_FILTERS = {
  restaurant_group_id: '',
  restaurant_sub_group_id: '',
  restaurant_ids: [],
}

const DEFAULT_SORT_BY = 'external_number'
const DEFAULT_SORT_ORDER = 'asc'

const TABLE_COLUMNS = [
  { key: 'id', sortable: true },
  { key: 'restaurant', sortable: true },
  { key: 'external_number', label: 'Guid', sortable: true },
  { key: 'name', sortable: true },
  { key: 'tips_flag', label: 'Micros Tips Flag', sortable: true },
  { key: 'charge_type', label: 'Type', sortable: true },
  { key: 'status', sortable: true },
]

const flattenDropdownPages = (data) => data.pages.flat()

const EMPTY_DROPDOWN_RESULT = []

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// The backend's own reason (e.g. "Orders for this restaurant do not come from Micros"), if any.
const responseReason = (error) => getMutationErrorMessage({ response: error?.response }, '')

export const ServiceCharges = () => {
  const location = useLocation()
  const navigate = useNavigate()
  // Captured once and cleared off the history entry, as on Meal Periods.
  const [navigatedRestaurant] = useState(() => location?.state?.restaurant)
  const navigatedGroupIdFromState = navigatedRestaurantGroupId(navigatedRestaurant)
  const navigatedGroupNameFromState = navigatedRestaurantGroupName(navigatedRestaurant)
  const singleRestaurantGroupAutoApplied = useRef(false)
  const singleRestaurantAutoApplied = useRef(false)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS)
  const [filtersChanged, setFiltersChanged] = useState(false)
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY)
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER)
  const [search, setSearch] = useState('')
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('')
  const [restaurantSubGroupSearch, setRestaurantSubGroupSearch] = useState('')
  const [restaurantSearch, setRestaurantSearch] = useState('')
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [restaurantSubGroupOptionsCache, setRestaurantSubGroupOptionsCache] = useState([])
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [csvLoading, setCsvLoading] = useState(false)
  const [typeMenuAnchorEl, setTypeMenuAnchorEl] = useState(null)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [rowLoadingId, setRowLoadingId] = useState(null)
  const [pendingChange, setPendingChange] = useState(null)
  // The selection as it is now, for code that resumes after an await.
  const latestFilters = useRef(EMPTY_FILTERS)
  latestFilters.current = filters
  const { ability } = useContext(AbilityContext)
  const { admin } = useSelector((state) => state.admin)
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const showNotification = useNotificationContext()
  const queryClient = useQueryClient()
  const debouncedSearchTerm = useDebounce(search, 300)
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedRestaurantSubGroupSearch = useDebounce(restaurantSubGroupSearch.trim(), 300)
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)

  const hasRestaurantGroupSelected = Boolean(filters?.restaurant_group_id)
  const hasAppliedSelection = Boolean(appliedFilters?.restaurant_group_id || appliedFilters?.restaurant_ids?.length > 0)

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurant_groups_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    })

    return response?.data?.map(normalizeRestaurantGroup) || []
  }

  const {
    data: restaurantGroups = EMPTY_DROPDOWN_RESULT,
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
      source: 'service_charges',
    }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchRestaurantSubGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurant_sub_groups_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSubGroupSearch || undefined,
        restaurant_group_id: filters?.restaurant_group_id,
      },
    })

    return response?.data?.map((subGroup) => ({
      id: subGroup?.value,
      name: subGroup?.label,
    })) || []
  }

  const {
    data: restaurantSubGroups = EMPTY_DROPDOWN_RESULT,
    isFetching: restaurantSubGroupFetching,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_sub_groups_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSubGroupSearch,
      source: 'service_charges',
    }),
    queryFn: fetchRestaurantSubGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    enabled: hasRestaurantGroupSelected,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchRestaurantList = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurants_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: filters?.restaurant_group_id || undefined,
        restaurant_sub_group_id: filters?.restaurant_sub_group_id || undefined,
      },
    })

    return response?.data?.map(normalizeRestaurant) || []
  }

  const {
    data: restaurants = EMPTY_DROPDOWN_RESULT,
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      restaurant_sub_group_id: filters?.restaurant_sub_group_id,
      search: debouncedRestaurantSearch,
      source: 'service_charges',
    }),
    queryFn: fetchRestaurantList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const { rootRef: restaurantSubGroupMenuRef, targetRef: restaurantSubGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const fetchServiceCharges = async () => {
    const response = await api.get('/v1/admin/configured_service_charges', {
      params: {
        ...appliedFilters,
        search: debouncedSearchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page: page,
        per_page: itemsPerPage,
      },
    })

    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const { data: serviceCharges, isFetching, isError } = useQuery({
    queryKey: Query_Keys.service_charges({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
      search: debouncedSearchTerm,
    }),
    queryFn: fetchServiceCharges,
    staleTime: 10 * 60 * 1000,
    enabled: hasAppliedSelection,
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'))
  }, [restaurantGroups])

  useEffect(() => {
    setRestaurantSubGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantSubGroups, 'id'))
  }, [restaurantSubGroups])

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'))
  }, [restaurants])

  // Selected restaurants the admin may import for. Derived, not stored: the ability loads after the first render.
  const permittedRestaurantIds = useMemo(() => {
    const selectedRestaurantIds = filters?.restaurant_ids || []

    if (superAdmin?.role === 'admin') return selectedRestaurantIds

    return selectedRestaurantIds
      .filter((id) => strictCanScope(ability, 'fetch_and_imports', 'Restaurant', `${id}`))
  }, [ability, superAdmin?.role, filters?.restaurant_ids])

  const fetchPermission = permittedRestaurantIds.length > 0

  // A navigated prefill can only land with an id, or a group that lets the restaurant dropdown
  // resolve the name. Without either, the page's own auto-apply stays live.
  const navigatedPrefillCanLand = Boolean(
    navigatedRestaurant?.id || (hasNavigatedRestaurant(navigatedRestaurant) && (navigatedGroupIdFromState || filters?.restaurant_group_id))
  )

  useEffect(() => {
    if (!location?.state?.restaurant) {
      return
    }

    navigate(location.pathname, { replace: true, state: null })
  }, [])

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && hasSingleItem(restaurantGroups)
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && hasSingleItem(restaurants)

  useEffect(() => {
    if (navigatedGroupIdFromState || filters?.restaurant_group_id || singleRestaurantGroupAutoApplied.current || !hasSingleAvailableRestaurantGroup) {
      return
    }

    singleRestaurantGroupAutoApplied.current = true
    setFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_group_id: getSingleItemValue(restaurantGroups)?.label,
    }))
    setFiltersChanged(true)
  }, [filters?.restaurant_group_id, hasSingleAvailableRestaurantGroup, navigatedGroupIdFromState, restaurantGroups])

  useEffect(() => {
    // Waits for settled options: placeholder data still holds the previous group's restaurants.
    if (
      navigatedPrefillCanLand
      || filters?.restaurant_ids?.length > 0
      || singleRestaurantAutoApplied.current
      || !hasSingleAvailableRestaurant
      || restaurantFetching
    ) {
      return
    }

    const singleRestaurant = getSingleItemValue(restaurants)

    singleRestaurantAutoApplied.current = true
    setFilters((currentFilters) => ({ ...currentFilters, restaurant_ids: [singleRestaurant?.id] }))
    setFiltersChanged(true)
  }, [filters?.restaurant_ids, hasSingleAvailableRestaurant, navigatedPrefillCanLand, restaurants, restaurantFetching])

  const handleRestaurantGroupChange = (event) => {
    singleRestaurantAutoApplied.current = false
    setRestaurantSubGroupSearch('')
    setRestaurantSearch('')
    setFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_group_id: event.target.value,
      restaurant_sub_group_id: '',
      restaurant_ids: [],
    }))
    setFiltersChanged(true)
  }

  const handleRestaurantGroupClear = () => {
    singleRestaurantGroupAutoApplied.current = true
    singleRestaurantAutoApplied.current = true
    setRestaurantSubGroupSearch('')
    setRestaurantSearch('')
    setFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_group_id: '',
      restaurant_sub_group_id: '',
      restaurant_ids: [],
    }))
    setFiltersChanged(true)
  }

  const handleRestaurantSubGroupChange = (event) => {
    singleRestaurantAutoApplied.current = false
    setRestaurantSearch('')
    setFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_sub_group_id: event.target.value,
      restaurant_ids: [],
    }))
    setFiltersChanged(true)
  }

  const handleRestaurantSubGroupClear = () => {
    singleRestaurantAutoApplied.current = true
    setRestaurantSearch('')
    setFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_sub_group_id: '',
      restaurant_ids: [],
    }))
    setFiltersChanged(true)
  }

  const handleRestaurantChange = (event) => {
    const selectedRestaurantIds = event.target.value || []

    setFilters((currentFilters) => ({ ...currentFilters, restaurant_ids: selectedRestaurantIds }))
    setFiltersChanged(true)
  }

  const handleRestaurantClear = () => {
    singleRestaurantAutoApplied.current = true
    setFilters((currentFilters) => ({ ...currentFilters, restaurant_ids: [] }))
    setFiltersChanged(true)
  }

  const applySelectedFilters = (nextFilters) => {
    setAppliedFilters(nextFilters)
    setFiltersChanged(false)
    setItemsPerPage(25)
    setPage(1)
  }

  useNavigatedRestaurantFilter({
    navigatedRestaurant,
    emptyFilters: EMPTY_FILTERS,
    restaurantOptionsCache,
    restaurantSearch,
    setFilters,
    setRestaurantSearch,
    applySelectedFilters,
  })

  const handleUpdate = () => {
    if (!filters?.restaurant_group_id && filters?.restaurant_ids?.length === 0) {
      showNotification('Please select a restaurant group or restaurant', 'error')
      return
    }

    applySelectedFilters(filters)
  }

  const handleReset = () => {
    singleRestaurantGroupAutoApplied.current = true
    singleRestaurantAutoApplied.current = true
    setFilters(EMPTY_FILTERS)
    setAppliedFilters(EMPTY_FILTERS)
    setFiltersChanged(false)
    setRestaurantGroupSearch('')
    setRestaurantSubGroupSearch('')
    setRestaurantSearch('')
    setSearch('')
    setSortBy(DEFAULT_SORT_BY)
    setSortOrder(DEFAULT_SORT_ORDER)
    setItemsPerPage(25)
    setPage(1)
  }

  const handleSearch = (searchQuery) => {
    setSearch(searchQuery)
    setPage(1)
  }

  // Falls back to the navigated group's name while it is beyond the first dropdown page.
  const getRestaurantGroupName = (id) => {
    const restaurantGroup = restaurantGroupOptionsCache?.find((group) => `${group?.label}` === `${id}`)
    if (restaurantGroup?.value) {
      return restaurantGroup.value
    }

    return `${navigatedGroupIdFromState}` === `${id}` ? navigatedGroupNameFromState || '' : ''
  }

  const getRestaurantSubGroupName = (id) => {
    const restaurantSubGroup = restaurantSubGroupOptionsCache?.find((subGroup) => `${subGroup?.id}` === `${id}`)
    return restaurantSubGroup?.name || ''
  }

  const getRestaurantName = (id) => {
    const restaurant = restaurantOptionsCache?.find((res) => `${res?.id}` === `${id}`)
    return restaurant?.name || ''
  }

  const getRestaurantDisplayValue = () => {
    const selectedRestaurantIds = filters?.restaurant_ids || []

    if (selectedRestaurantIds.length === 0) return ''
    if (selectedRestaurantIds.length === 1) return getRestaurantName(selectedRestaurantIds[0])

    return `${selectedRestaurantIds.length} Restaurants Selected`
  }

  const selectedRestaurantGroupOption = restaurantGroupOptionsCache?.find(
    (group) => `${group?.label}` === `${filters?.restaurant_group_id}`
  )
  const displayedRestaurantGroups = restaurantGroupSearch.trim()
    ? restaurantGroups
    : selectedRestaurantGroupOption
      ? mergeUniqueByKey(restaurantGroups, [selectedRestaurantGroupOption], 'label')
      : restaurantGroups

  const selectedRestaurantSubGroupOption = restaurantSubGroupOptionsCache?.find(
    (subGroup) => `${subGroup?.id}` === `${filters?.restaurant_sub_group_id}`
  )
  const displayedRestaurantSubGroups = restaurantSubGroupSearch.trim()
    ? restaurantSubGroups
    : selectedRestaurantSubGroupOption
      ? mergeUniqueByKey(restaurantSubGroups, [selectedRestaurantSubGroupOption], 'id')
      : restaurantSubGroups

  const selectedRestaurantOptions = restaurantOptionsCache?.filter((restaurant) => (
    filters?.restaurant_ids?.some((id) => `${restaurant?.id}` === `${id}`)
  ))
  const displayedRestaurants = restaurantSearch.trim()
    ? restaurants
    : mergeUniqueByKey(restaurants, selectedRestaurantOptions, 'id')

  const { mutateAsync: fetchServiceChargesMutation } = useOtMutation({
    mutationFn: async (restaurantId) => await api.post(
      '/v1/admin/configured_service_charges/fetch_configured_service_charges',
      { restaurant_id: restaurantId }
    ),
    invalidateKeys: serviceChargeMutationKeyGroups.fetch,
    successMessage: ({ variables: restaurantId }) => `Service charges imported successfully for ${getRestaurantName(restaurantId)}`,
    errorMessage: ({ error, variables: restaurantId }) => {
      const message = `Failed to import service charges for ${getRestaurantName(restaurantId)}`
      const reason = responseReason(error)

      return reason ? `${message}: ${reason}` : message
    },
    onSettled: () => {
      invalidateAuditLogQueries(queryClient);
    },
  })

  const handleManualImport = async () => {
    const selectedRestaurantIds = filters?.restaurant_ids || []

    if (selectedRestaurantIds.length === 0) {
      showNotification('Please select at least one restaurant', 'error')
      return
    }

    const skippedCount = selectedRestaurantIds.length - permittedRestaurantIds.length

    if (skippedCount > 0) {
      showNotification(
        `Skipped ${skippedCount} of ${selectedRestaurantIds.length} restaurants — you do not have import permission for them`,
        'warning'
      )
    }

    if (permittedRestaurantIds.length === 0) return

    setIsLoading(true)

    let importedAny = false

    try {
      for (const [index, restaurantId] of permittedRestaurantIds.entries()) {
        // One request at a time, paced: each one calls Micros inline.
        if (index > 0) await delay(3500)

        try {
          await fetchServiceChargesMutation(restaurantId)
          importedAny = true
        } catch {
          // Reported by useOtMutation onError; the next restaurants still run.
        }
      }
    } finally {
      setIsLoading(false)
    }

    // Show what was just imported when the selection was not applied yet, unless the selection
    // changed while the fetch ran: that newer choice stays pending for Update.
    if (importedAny && (!hasAppliedSelection || filtersChanged) && latestFilters.current === filters) {
      applySelectedFilters(filters)
    }
  }

  const { mutateAsync: updateChargeTypeMutation } = useOtMutation({
    mutationFn: async ({ id, chargeType }) => await api.patch(
      `/v1/admin/configured_service_charges/${id}`,
      { charge_type: chargeType }
    ),
    invalidateKeys: serviceChargeMutationKeyGroups.update,
    onSuccess: async (_data, { recalculates }) => {
      if (recalculates) {
        await invalidateQueryKeys(queryClient, serviceChargeMutationKeyGroups.recalculate)
      }
    },
    successMessage: ({ variables }) => (
      variables.recalculates
        ? 'Service charge updated. Past orders are being recalculated in the background.'
        : 'Service charge updated successfully'
    ),
    errorMessage: ({ error }) => {
      const reason = responseReason(error)

      return reason ? `Failed to update the service charge: ${reason}` : 'Failed to update the service charge'
    },
  })

  const handleTypeMenuOpen = (event, record) => {
    if (rowLoadingId) return

    setTypeMenuAnchorEl(event.currentTarget)
    setSelectedRecord(record)
  }

  const handleTypeMenuClose = () => {
    setTypeMenuAnchorEl(null)
    setSelectedRecord(null)
  }

  const saveChargeType = async (record, chargeType, recalculates = false) => {
    setRowLoadingId(record.id)

    try {
      await updateChargeTypeMutation({ id: record.id, chargeType, recalculates })
    } catch {
      // Reported by useOtMutation onError.
    } finally {
      setRowLoadingId(null)
    }
  }

  // A change into or out of Fee or Tip, judged from the row's loaded type, asks first with the past orders' count.
  const handleTypeSelect = async (record, chargeType) => {
    // Closed before awaiting: the refetch unmounts the anchor.
    handleTypeMenuClose()

    if (!record?.id || record.charge_type === chargeType) return

    if (!recalculatesPastOrders(record.charge_type, chargeType)) {
      await saveChargeType(record, chargeType)
      return
    }

    setRowLoadingId(record.id)

    let count

    try {
      const response = await api.get(`/v1/admin/configured_service_charges/${record.id}/orders_count`)
      count = response?.data
    } catch {
      showNotification('Could not count the past orders for this charge. Please retry.', 'error')
      return
    } finally {
      setRowLoadingId(null)
    }

    setPendingChange({ record, chargeType, count })
  }

  const handleConfirmChange = async () => {
    if (!pendingChange) return

    const { record, chargeType, count } = pendingChange

    setPendingChange(null)
    await saveChargeType(record, chargeType, hasPastOrders(count))
  }

  const handleExportCsv = async () => {
    if (csvLoading) return

    try {
      setCsvLoading(true)

      const query = buildQueryParams({
        ...appliedFilters,
        search: debouncedSearchTerm,
        sort_by: sortBy,
        sort_order: sortOrder,
      })
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=configured_service_charges&${query}`

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV',
      })

      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `service_charges_${dayjs().format('YYYY-MM-DD')}.csv`
      a.click()

      window.URL.revokeObjectURL(downloadUrl)
      showNotification('CSV downloaded successfully', 'success')
    } catch (error) {
      console.error('CSV download error:', error)
      showNotification(error?.message || 'Failed to download CSV', 'error')
    } finally {
      setCsvLoading(false)
    }
  }

  const columns = TABLE_COLUMNS.map((column) => ({
    key: column.key,
    label: column.label || camelize(column.key),
    sortable: column.sortable,
  }))

  const TableRows = (data) => {
    return data?.map((record) => {
      const canUpdate = strictCanScope(ability, 'update', 'Restaurant', `${record?.restaurant_id}`)

      return {
        id: record?.id,
        restaurant: record?.restaurant?.name || '-',
        external_number: record?.external_number ?? '-',
        name: record?.name || '-',
        tips_flag: record?.tips_flag ? 'Yes' : 'No',
        charge_type: {
          display: (
            <StyledStatusButton
              disableRipple
              endIcon={canUpdate ? <KeyboardArrowDownIcon sx={{ color: 'grey' }} /> : null}
              onClick={(event) => handleTypeMenuOpen(event, record)}
              disabled={!canUpdate || rowLoadingId === record?.id}
              data-testid={`service-charge-type-${record?.id}`}
            >
              <ChargeTypePill chargeType={record?.charge_type}>
                {chargeTypeLabel(record?.charge_type)}
              </ChargeTypePill>
            </StyledStatusButton>
          ),
          sortValue: record?.charge_type,
        },
        status: {
          display: (
            <StatusText status={record?.status} data-testid={`service-charge-status-${record?.id}`}>
              {STATUS_LABELS[record?.status] || '-'}
            </StatusText>
          ),
          sortValue: record?.status,
        },
      }
    })
  }

  const rows = TableRows(serviceCharges?.data)
  const hasSearchTerm = !!debouncedSearchTerm

  const handleSortChange = (columnKey, direction) => {
    setSortBy(columnKey)
    setSortOrder(direction)
    setPage(1)
  }

  const hasPendingOrders = hasPastOrders(pendingChange?.count)
  const pendingNote = typeChangeNote(pendingChange?.record?.charge_type, pendingChange?.chargeType, hasPendingOrders)
  const pendingDialogBody = hasPendingOrders
    ? `Past orders to recalculate: ${formatOrdersCount(pendingChange?.count)}. ${pendingNote}`
    : `No past orders carry this charge yet. ${pendingNote}`

  return (
    <ServiceChargePage>
      {/* Bread crumbs */}
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color={"#515152"}>Restaurant Management</StyledLink>
          <StyledLink color={"#515152"}>Service Charge</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      {/* Filters */}
      <FilterContainer>
        <Box sx={filterContainerStyle}>
          <Box sx={filterSectionStyle}>
            <Typography variant="body2" sx={typographyStyle}>
              View
            </Typography>

            {/* Restaurant group filter */}
            <StyledFormControl size="small">
              <StyledInputLabel>Restaurant Group</StyledInputLabel>
              <Select
                data-testid="service-charges-restaurant-group-filter"
                size="small"
                label="Restaurant Group"
                MenuProps={{
                  ...menuPropStyle,
                  PaperProps: {
                    ...menuPropStyle.PaperProps,
                    ref: restaurantGroupMenuRef,
                  },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "160px" },
                  width: { xs: "100%", sm: "160px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantGroupChange}
                onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                value={filters?.restaurant_group_id || ""}
                renderValue={() => getRestaurantGroupName(filters?.restaurant_group_id)}
                endAdornment={
                  filters?.restaurant_group_id ? (
                    <StyledFilterCancel position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        disableRipple
                        onClick={handleRestaurantGroupClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </StyledFilterCancel>
                  ) : null
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                {displayedRestaurantGroups.map((res) => (
                  <StyledMenuItem
                    key={res?.label}
                    value={res?.label}
                  >
                    {
                      `${filters?.restaurant_group_id}` === `${res?.label}` ? <SelectedMenuItem data={res?.value} /> : res?.value
                    }
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantGroupsPage}
                  targetRef={restaurantGroupLoaderRef}
                />
              </Select>
            </StyledFormControl>

            {/* Restaurant sub group filter */}
            <StyledFormControl size="small" disabled={!hasRestaurantGroupSelected}>
              <StyledInputLabel>Restaurant Sub Group</StyledInputLabel>
              <Select
                data-testid="service-charges-restaurant-sub-group-filter"
                size="small"
                label="Restaurant Sub Group"
                MenuProps={{
                  ...menuPropStyle,
                  PaperProps: {
                    ...menuPropStyle.PaperProps,
                    ref: restaurantSubGroupMenuRef,
                  },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "215px" },
                  width: { xs: "100%", sm: "215px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantSubGroupChange}
                onClose={() => setTimeout(() => setRestaurantSubGroupSearch(''), 1000)}
                value={filters?.restaurant_sub_group_id || ""}
                renderValue={() => getRestaurantSubGroupName(filters?.restaurant_sub_group_id)}
                endAdornment={
                  filters?.restaurant_sub_group_id ? (
                    <StyledFilterCancel position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        disableRipple
                        onClick={handleRestaurantSubGroupClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </StyledFilterCancel>
                  ) : null
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantSubGroupSearch} placeholder={'Search restaurant sub group'} isFetching={restaurantSubGroupFetching} />
                {displayedRestaurantSubGroups.map((res) => (
                  <StyledMenuItem
                    key={res?.id}
                    value={res?.id}
                  >
                    {
                      `${filters?.restaurant_sub_group_id}` === `${res?.id}` ? <SelectedMenuItem data={res?.name} /> : res?.name
                    }
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantSubGroupsPage}
                  targetRef={restaurantSubGroupLoaderRef}
                />
              </Select>
            </StyledFormControl>

            {/* Restaurant filter */}
            <StyledFormControl size="small">
              <StyledInputLabel>Restaurant</StyledInputLabel>
              <Select
                data-testid="service-charges-restaurant-filter"
                size="small"
                label="Restaurant"
                multiple
                MenuProps={{
                  ...menuPropStyle,
                  PaperProps: {
                    ...menuPropStyle.PaperProps,
                    ref: restaurantMenuRef,
                  },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: { xs: "100%", sm: "160px" },
                  width: { xs: "100%", sm: "160px" },
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantChange}
                onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                value={filters?.restaurant_ids || []}
                renderValue={() => getRestaurantDisplayValue()}
                endAdornment={
                  filters?.restaurant_ids?.length > 0 ? (
                    <StyledFilterCancel position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        disableRipple
                        onClick={handleRestaurantClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </StyledFilterCancel>
                  ) : null
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                {displayedRestaurants.map((res) => (
                  <StyledMenuItem
                    key={res?.id}
                    value={res?.id}
                  >
                    {
                      filters?.restaurant_ids?.includes(res?.id) ? <SelectedMenuItem data={res?.name} /> : res?.name
                    }
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantsPage}
                  targetRef={restaurantLoaderRef}
                />
              </Select>
            </StyledFormControl>
          </Box>

          <Box sx={actionSectionStyle}>
            <ResetButton
              onClick={handleReset}
              status={isFetching}
            />

            <UpdateButton
              onClick={handleUpdate}
              filtersChanged={filtersChanged}
              status={isFetching}
            />
          </Box>
        </Box>
      </FilterContainer>

      {/* Page title and search */}
      <Box sx={titleSectionStyle}>
        <Typography variant="body2" sx={pageTitleStyle} data-testid="page-title">
          Service Charge
        </Typography>

        <StyledSearchBar
          value={search}
          placeholder="Search id, guid or name"
          disabled={!hasAppliedSelection}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
          }}
          onChange={(event) => handleSearch(event.target.value)}
          autoComplete="off"
          inputProps={{ "data-testid": "service-charges-search-input" }}
          sx={{ mt: '8px' }}
        />
      </Box>

      <Divider sx={{ borderStyle: 'solid', borderWidth: '1px', marginBottom: '25px' }} />

      {/* Manual import */}
      <FetchButtonContainer>
        <Button
          data-testid="service-charges-manual-import-btn"
          sx={fetchButtonStyle}
          variant="contained"
          onClick={handleManualImport}
          disabled={!fetchPermission || isLoading}
        >
          {isLoading ? <LoadingSpinner isLoading={true} /> : <CachedIcon />}
        </Button>
      </FetchButtonContainer>

      {/* Index table */}
      {isFetching && !hasSearchTerm ? (
        <Loader />
      ) : isError ? (
        <div>
          <Alert
            data-testid="service-charges-error-message"
            severity="error"
          >
            Could not load service charges. Please retry.
          </Alert>
        </div>
      ) : (hasAppliedSelection && serviceCharges?.data) ? (
        <IndexTable
          columns={columns}
          data={rows}
          tableId="service-charges"
          type="entries"
          report={false}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          pageCount={serviceCharges?.pageCount}
          searchLoading={isFetching && hasSearchTerm}
          hasSearchTerm={hasSearchTerm}
          showDownloadButton={true}
          disableDownloadButton={serviceCharges?.data?.length === 0 || csvLoading}
          handleExportCsv={handleExportCsv}
          csvLoading={csvLoading}
          serverSort={true}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        />
      ) : (
        <div>
          <Alert
            data-testid="service-charges-alert-message"
            severity="info"
          >
            Select a restaurant group or restaurant
          </Alert>
        </div>
      )}

      <Popover
        open={Boolean(typeMenuAnchorEl)}
        anchorEl={typeMenuAnchorEl}
        onClose={handleTypeMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: statusPopoverPaperStyle }}
        data-testid="service-charge-type-menu"
      >
        <StatusMenuList dense disablePadding>
          {CHARGE_TYPE_OPTIONS.map((option) => (
            <StyledMenuItem
              key={option.value}
              disabled={selectedRecord?.charge_type === option.value}
              onClick={() => handleTypeSelect(selectedRecord, option.value)}
              sx={statusMenuItemStyle}
              data-testid={`service-charge-type-option-${option.value}`}
            >
              {option.label}
            </StyledMenuItem>
          ))}
        </StatusMenuList>
      </Popover>

      <ConfirmationDialog
        open={Boolean(pendingChange)}
        onClose={() => setPendingChange(null)}
        Icon={CheckIcon}
        dialogTitle={`Change "${pendingChange?.record?.name || pendingChange?.record?.external_number || ''}" to ${chargeTypeLabel(pendingChange?.chargeType)}?`}
        dialogBody={pendingDialogBody}
        confirmationText={hasPendingOrders ? 'Yes, recalculate the past orders' : 'Yes, change the type'}
        handleConfirm={handleConfirmChange}
        dataTestId="service-charge-type-confirmation-dialog"
      />
    </ServiceChargePage>
  )
}
